/**
 * How to Style — sections/how-to-style.liquid
 *
 * Isolation: every listener here is bound to a specific section root
 * (`.how-to-style-section`), never to `document`. Each section instance on
 * the page gets its own independent init pass, so this file works correctly
 * even if the section appears more than once, and none of it can be
 * triggered by or interfere with unrelated components such as
 * snippets/product-grid-item.liquid's own `.js-open-popup` popups.
 *
 * Modal: one shared dialog per section instance. Opening a Look clones that
 * Look's <template> (rendered in sections/how-to-style.liquid) into the
 * dialog's body rather than each Look owning its own dialog — see the
 * comment at the top of that section file.
 *
 * Variant resolution: mirrors the core matching primitive already used by
 * the theme's own PDP/grid variant pickers (snippets/pdp-variant-picker.liquid,
 * snippets/product-grid-variant-picker.liquid) — case-insensitive comparison
 * of selected option values against `variant.option1/2/3` — reading from the
 * section's single deduplicated product-data store
 * (data-how-to-style-product-data) instead of a live fetch. Money is
 * formatted via the theme's own existing global helper, window.formatMoney
 * (assets/currency.js) — the same one gocart.js itself uses for cart totals
 * — not a bespoke formatter, so display always matches the rest of the
 * storefront.
 *
 * Add to Cart: reuses the theme's existing GoCart implementation completely.
 * gocart.js's own setupAtcButtons() binds to `.js-go-cart-add-to-cart`
 * elements once at DOMContentLoaded — before our modal content is ever
 * cloned into the DOM — so it can't see these buttons on its own. This file
 * does not reimplement any cart logic: it just performs the identical
 * lookup GoCart's own click handler does (walk up to the enclosing <form>,
 * read its id) and calls the real window.goCart.addItemToCart(formId)
 * (window.goCart is exposed in layout/theme.liquid for exactly this reason).
 * This section's only responsibility is keeping the hidden variant-id input
 * correct before that call happens.
 *
 * Theme Editor: init only ever runs once per root, guarded by
 * `root.dataset.htsInitialized`. shopify:section:load re-runs init scoped to
 * just the reloaded section's wrapper (event.target) — this covers both a
 * settings change (Shopify replaces the section's HTML and dispatches this)
 * and a fresh "Add section" insert, without a page refresh, and without
 * double-binding listeners on sections that weren't touched.
 * shopify:section:unload/:deselect force-close this section's modal if it
 * happens to be open, since document.body.classList.add('how-to-style-modal-open')
 * touches an element outside the section root that wouldn't otherwise get
 * cleaned up if the section's own DOM is replaced/removed mid-open.
 * shopify:section:select needs no special handling — there's no
 * autoplay/timer state to pause or resume, and init is idempotent regardless.
 *
 * No dependency on jQuery or the removed Stylitics widget scripts.
 *
 * Color-aware visibility (max 3 Looks, switches on variant/color change):
 * each .how-to-style-grid-item carries data-hts-legacy and
 * data-hts-variant-ids (rendered in sections/how-to-style.liquid from the
 * Look's applicable_variant_ids field). A legacy Look (data-hts-legacy
 * "true" — every pre-existing Look, created before that field existed)
 * always applies, matching the original product-level behavior. A
 * variant-aware Look only applies when the currently selected variant's id
 * is in its list. At most 3 applicable Looks are ever visible; if none
 * apply to the selected variant, the whole section is hidden — no empty
 * heading or container is ever left showing.
 *
 * getSelectedVariantId() deliberately does not read any state produced by
 * assets/product.js (e.g. its `variants`/`cr` globals or the
 * input.product-variant-id it updates) — that would depend on script
 * execution/listener-registration order between two separate files, which
 * isn't guaranteed. Instead it independently recomputes the selected
 * variant from the same raw inputs the theme's own picker uses: the global
 * `variants` array that pdp-variant-picker.liquid's inline script assigns
 * (a plain `var`, so it's on window and already populated before any user
 * interaction is possible) and the live DOM state of whichever picker the
 * product actually rendered (<variant-radios> or <variant-selects>, per
 * product.has_only_default_variant in that same snippet). The one
 * document-level listener this requires (variant pickers live outside this
 * section, in the product form) is bound once, guarded the same way as the
 * shopify:section:* listeners below, and only ever reads that state and
 * updates this section's own visibility — never anything outside it.
 */
(function () {
  'use strict';

  var MAX_VISIBLE_LOOKS = 3;

  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getSelectedVariantId() {
    var variants = window.variants;
    if (!Array.isArray(variants) || !variants.length) return null;
    if (variants.length === 1) return variants[0].id;

    var values = [];
    var radiosRoot = document.querySelector('variant-radios');
    var selectsRoot = document.querySelector('variant-selects');

    if (radiosRoot) {
      Array.prototype.forEach.call(radiosRoot.querySelectorAll('input:checked'), function (input) {
        values.push(input.value);
      });
    } else if (selectsRoot) {
      Array.prototype.forEach.call(selectsRoot.querySelectorAll('select'), function (select) {
        values.push(select.value);
      });
    } else {
      // No picker rendered at all — has_only_default_variant path.
      return variants[0].id;
    }

    var matched = variants.filter(function (variant) {
      return variant.options.every(function (optionValue, index) {
        return optionValue === values[index];
      });
    })[0];

    return matched ? matched.id : null;
  }

  function updateVisibleLooks(root, selectedVariantId) {
    var items = Array.prototype.slice.call(root.querySelectorAll('.how-to-style-grid-item'));
    var selectedVariantIdString = selectedVariantId == null ? null : String(selectedVariantId);

    var applicable = items.filter(function (item) {
      if (item.dataset.htsLegacy === 'true') return true;
      if (selectedVariantIdString === null) return false;
      var variantIds = (item.dataset.htsVariantIds || '').split(',').filter(Boolean);
      return variantIds.indexOf(selectedVariantIdString) !== -1;
    });

    var visible = applicable.slice(0, MAX_VISIBLE_LOOKS);

    items.forEach(function (item) {
      item.hidden = visible.indexOf(item) === -1;
    });

    root.hidden = visible.length === 0;

    // The "How to Style This" image badge (snippets/pdp-photos.liquid) is
    // never a separate source of truth — it always mirrors this exact
    // same visible.length > 0 decision, via this event.
    document.dispatchEvent(
      new CustomEvent('how-to-style:visibility-changed', { detail: { visible: visible.length > 0 } })
    );
  }

  // Cross-product color swap ------------------------------------------------
  //
  // On this theme, a product's "colors" are frequently modeled as separate
  // linked Shopify products (see pdp-variant-picker.liquid's
  // renderColorSwatches()/fetchProductByOptions()/updateProductDetails()),
  // not same-product variants — confirmed by inspecting real Liverpool PDP
  // data (e.g. "V-NECK WOVEN TANK W/ LACE TRIM" links to a second, entirely
  // separate product for its other color, via
  // product.metafields.related_products.product_list). A plain variant
  // 'change' event never fires for this — .color-swatch inputs (built by
  // that snippet's own JS into #color-swatches-container) sit outside
  // .product-form__input entirely, and switching "color" there means the
  // active PRODUCT itself has changed. getSelectedVariantId()'s
  // window.variants/DOM-option matching can't help here: it has no way to
  // know a different product's variants at all.
  //
  // The fix reuses Shopify's own Section Rendering API (?section_id=...) —
  // not a custom endpoint or a client-side reimplementation of Look
  // matching — so the swapped-in markup is a genuine fresh Liquid render,
  // computed by the exact same server-side logic as a normal page load.
  // Once a swap has happened, crossProductState remembers the active
  // product so a SUBSEQUENT plain size change (still firing on the
  // original, never-replaced .product-form__input radios/selects, which
  // this theme does not swap) also re-fetches against the correct product,
  // instead of silently resolving against the original page's variants.
  var crossProductState = null; // { productUrl } once any color swap has occurred, else null

  function getCurrentSectionId() {
    var section = document.querySelector('.how-to-style-section');
    return section ? section.dataset.sectionId : null;
  }

  function getCurrentlyCheckedSize() {
    var checked = document.querySelector('.size-variant input:checked');
    return checked ? checked.value : null;
  }

  // Mirrors fetchProductByOptions()'s own size-matching in
  // pdp-variant-picker.liquid closely enough for our purposes: given the
  // target product's own variants (fetched fresh, since it's a different
  // product with its own variant set) and whatever size is currently
  // checked, finds the same-size variant on the NEW product; falls back to
  // its first variant if no match (e.g. a size this color doesn't carry).
  function findVariantForCheckedSize(productVariants, checkedSize) {
    if (!checkedSize) return productVariants[0];
    var lowerSize = checkedSize.toLowerCase();
    var matched = productVariants.filter(function (variant) {
      return (variant.options || []).some(function (option) {
        return String(option).toLowerCase() === lowerSize;
      });
    })[0];
    return matched || productVariants[0];
  }

  // Each swap is two sequential network round trips, so clicking through
  // several colors quickly means multiple swaps are in flight at once —
  // and network timing does not guarantee they resolve in the order they
  // were started. Without a guard, an earlier click's response can arrive
  // and apply AFTER a later click's, leaving the widget showing the wrong
  // color's Looks even though every individual fetch succeeded (confirmed
  // as the actual cause of a real "selected black, widget shows blue"
  // report). swapRequestToken is bumped on every call; a response is only
  // ever applied if its own token still matches the latest one by the time
  // it resolves — a superseded (stale) response is silently discarded.
  var swapRequestToken = 0;

  function swapHowToStyleSectionForProduct(productUrl) {
    var sectionId = getCurrentSectionId();
    if (!sectionId) return Promise.resolve(); // this template has no how-to-style section block at all

    var requestToken = ++swapRequestToken;

    // Cache-busted (both here and on the section fetch below): confirmed via
    // real testing that Shopify's own CDN can serve a stale cached response
    // for a ?section_id=...&variant=... GET request, returning 200 with the
    // PREVIOUS variant's content instead of the one just requested. A fresh
    // query string on every request is the reliable fix — { cache: 'no-store' }
    // alone only stops the browser's own HTTP cache, not the CDN edge cache
    // in front of it.
    var cacheBuster = 'hts_cb=' + Date.now();

    return fetch(productUrl + '.js?' + cacheBuster, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      })
      .then(function (product) {
        var variantId = null;
        if (product && Array.isArray(product.variants) && product.variants.length) {
          var matched = findVariantForCheckedSize(product.variants, getCurrentlyCheckedSize());
          variantId = matched ? matched.id : null;
        }

        var sectionUrl =
          productUrl +
          '?section_id=' +
          encodeURIComponent(sectionId) +
          (variantId ? '&variant=' + variantId : '') +
          '&' + cacheBuster;

        return fetch(sectionUrl, { credentials: 'same-origin', cache: 'no-store' });
      })
      .then(function (response) {
        return response && response.ok ? response.text() : null;
      })
      .then(function (sectionHtml) {
        if (!sectionHtml) return;

        // A newer swap has started since this one began — this response is
        // stale even though it arrived successfully; applying it now would
        // silently overwrite the correct, more recent result.
        if (requestToken !== swapRequestToken) return;

        var currentSection = document.querySelector('.how-to-style-section');
        if (!currentSection) return;
        var wrapper = currentSection.closest('[id^="shopify-section-"]') || currentSection;

        var temp = document.createElement('div');
        temp.innerHTML = sectionHtml;
        var newWrapper = temp.firstElementChild;
        if (!newWrapper) return;

        wrapper.replaceWith(newWrapper);
        // The replaced node is brand new (never initialized), so
        // initSection's own idempotency guard just runs it fresh — nothing
        // else on the page gets touched or re-initialized twice.
        initAllSectionsWithin(document);

        crossProductState = { productUrl: productUrl };
      })
      .catch(function () {
        // Network/parse failure: leave whatever was already showing in
        // place rather than guessing — same fail-safe spirit as the rest
        // of this file (e.g. GoCart's own error handling).
      });
  }

  function getFocusableElements(container) {
    return Array.prototype.slice
      .call(container.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(function (element) {
        return element.offsetParent !== null;
      });
  }

  function readProductDataStore(root) {
    var script = root.querySelector('[data-how-to-style-product-data]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent) || {};
    } catch (error) {
      return {};
    }
  }

  // Only a Size picker is ever rendered (see snippets/how-to-style-modal-item.liquid) —
  // any other option position (e.g. Color) has no radios to check, so it's pinned to
  // this row's original default variant's value via data-base-optionN, and only the
  // Size position (data-size-position) is actually resolved from user selection.
  function findMatchingVariant(variants, row) {
    var sizePosition = row.dataset.sizePosition || null;
    var selectedSize = null;

    if (sizePosition) {
      var checked = row.querySelector('.how-to-style-option-input:checked');
      if (checked) selectedSize = checked.value.toLowerCase();
    }

    var pinned = {
      '1': row.dataset.baseOption1,
      '2': row.dataset.baseOption2,
      '3': row.dataset.baseOption3
    };

    return variants.filter(function (variant) {
      for (var position = 1; position <= 3; position++) {
        var key = String(position);
        var variantValue = variant['option' + position];

        if (key === sizePosition) {
          if (selectedSize !== null && (!variantValue || variantValue.toLowerCase() !== selectedSize)) {
            return false;
          }
        } else if (pinned[key] !== undefined) {
          if (!variantValue || variantValue.toLowerCase() !== pinned[key].toLowerCase()) {
            return false;
          }
        }
      }
      return true;
    })[0];
  }

  function updateRowVariant(row, productData, moneyFormat) {
    var variants = productData[row.dataset.productId];
    if (!variants || !variants.length) return;

    var matchedVariant = findMatchingVariant(variants, row);
    if (!matchedVariant) return;

    var hiddenInput = row.querySelector('[data-how-to-style-selected-variant-id]');
    if (hiddenInput) hiddenInput.value = matchedVariant.id;

    if (row.dataset.showPrice === 'true' && typeof window.formatMoney === 'function') {
      var currentPriceEl = row.querySelector('[data-how-to-style-current-price]');
      if (currentPriceEl) {
        currentPriceEl.textContent = window.formatMoney(matchedVariant.price / 100, moneyFormat);
      }

      var comparePriceEl = row.querySelector('[data-how-to-style-compare-price]');
      if (comparePriceEl) {
        var shouldShowCompare = Boolean(matchedVariant.compare_at_price) && row.dataset.showComparePrice === 'true';
        comparePriceEl.hidden = !shouldShowCompare;
        if (shouldShowCompare) {
          comparePriceEl.textContent = window.formatMoney(matchedVariant.compare_at_price / 100, moneyFormat);
        }
      }
    }

    var availabilityEl = row.querySelector('[data-how-to-style-availability]');
    if (availabilityEl) availabilityEl.hidden = matchedVariant.available;

    var submitButton = row.querySelector('.js-go-cart-add-to-cart');
    if (submitButton) {
      submitButton.disabled = !matchedVariant.available;
      var buttonText = submitButton.querySelector('[data-add-to-cart-text]');
      if (buttonText) buttonText.textContent = matchedVariant.available ? 'Add to Cart' : 'Sold Out';
    }
  }

  function initSection(root) {
    // Idempotency guard: shopify:section:load can fire for a section whose
    // root is already initialized (e.g. a :select immediately followed by a
    // :load in some editor flows). Without this, a second initSection call
    // would attach a second, independent set of delegated listeners to the
    // same still-live root — the exact "duplicate listener" failure mode.
    if (root.dataset.htsInitialized === 'true') return;
    root.dataset.htsInitialized = 'true';

    // Re-derive visibility independently on init rather than trusting the
    // server-rendered initial state blindly — cheap, and it self-corrects
    // in the rare case the client-computed selected variant differs from
    // product.selected_or_first_available_variant (e.g. a ?variant= URL
    // the Liquid render didn't account for).
    updateVisibleLooks(root, getSelectedVariantId());

    var modal = root.querySelector('.how-to-style-modal');
    if (!modal) return;

    var modalPanel = modal.querySelector('.how-to-style-modal-panel');
    var modalBody = modal.querySelector('.how-to-style-modal-body');
    var moneyFormat = root.dataset.moneyFormat;
    var productData = readProductDataStore(root);
    var lastTrigger = null;

    // Desktop only (matches the CSS breakpoint that drops the backdrop and
    // switches the panel to position:fixed with JS-driven top/left). Below
    // this width the panel stays a CSS-only bottom sheet — nothing to compute.
    var ANCHOR_MEDIA_QUERY = '(min-width: 1023px)';

    // Positions the panel next to the trigger that opened it, clamped to
    // stay fully inside the viewport. Safe to compute only once per open
    // (not on scroll) because body scroll is locked while the modal is
    // open; a resize listener (added in openModal, removed in closeModal)
    // covers the one case where the layout can still change meanwhile.
    function positionPanelNearTrigger() {
      if (!lastTrigger || !window.matchMedia(ANCHOR_MEDIA_QUERY).matches) {
        modalPanel.style.top = '';
        modalPanel.style.left = '';
        modalPanel.style.maxHeight = '';
        return;
      }

      var margin = 16;
      var gap = 12;
      var triggerRect = lastTrigger.getBoundingClientRect();

      // Cap the panel to the trigger card's own height — never taller than
      // the card that opened it — before measuring panelRect below, so the
      // vertical clamping further down reflects this capped height rather
      // than the panel's uncapped, potentially-much-taller natural height.
      // Content beyond this height scrolls inside .how-to-style-modal-body
      // (already overflow-y: auto), not by growing the panel.
      modalPanel.style.maxHeight = triggerRect.height + 'px';

      var panelRect = modalPanel.getBoundingClientRect();

      // Prefer opening to the right of the trigger; flip to its left when
      // there isn't room (e.g. the last card in a row, near the viewport's
      // right edge) — whichever side actually has enough space, not a fixed
      // "always right" choice. If neither side fully fits, use whichever
      // side has more room and clamp within the viewport margins.
      var spaceRight = window.innerWidth - triggerRect.right - margin;
      var spaceLeft = triggerRect.left - margin;
      var left;

      if (panelRect.width <= spaceRight) {
        left = triggerRect.right + gap;
      } else if (panelRect.width <= spaceLeft) {
        left = triggerRect.left - gap - panelRect.width;
      } else if (spaceRight >= spaceLeft) {
        left = window.innerWidth - panelRect.width - margin;
      } else {
        left = margin;
      }
      if (left < margin) left = margin;

      var top = triggerRect.top;
      if (top + panelRect.height > window.innerHeight - margin) {
        top = window.innerHeight - panelRect.height - margin;
      }
      if (top < margin) top = margin;

      modalPanel.style.left = left + 'px';
      modalPanel.style.top = top + 'px';
    }

    function handleModalKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key !== 'Tab') return;

      var focusable = getFocusableElements(modal);
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // Every open clones fresh from the pristine <template> and every close empties
    // modalBody entirely — the source <template> itself is never mutated (cloning
    // reads it, never writes to it), so there is nothing selection/availability/
    // button-state/etc. to "reset": each open is structurally a brand new render,
    // equivalent to a fresh product view, with no path for a previous open's
    // selections to leak into the next one — including across different Looks.
    function openModal(templateId, trigger) {
      var template = root.querySelector('#' + CSS.escape(templateId));
      if (!template) return;

      modalBody.innerHTML = '';
      modalBody.appendChild(template.content.cloneNode(true));

      lastTrigger = trigger || null;

      modal.style.display = 'block';
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('how-to-style-modal-open');
      modal.addEventListener('keydown', handleModalKeydown);
      window.addEventListener('resize', positionPanelNearTrigger);

      // Only meaningful once the panel is actually laid out (display:block
      // above) and sized to its content, so this runs last.
      positionPanelNearTrigger();

      var closeButton = modal.querySelector('.how-to-style-modal-close');
      if (closeButton) closeButton.focus();
    }

    function closeModal() {
      modal.style.display = 'none';
      modal.setAttribute('aria-hidden', 'true');
      modal.removeEventListener('keydown', handleModalKeydown);
      window.removeEventListener('resize', positionPanelNearTrigger);
      modalPanel.style.top = '';
      modalPanel.style.left = '';
      modalPanel.style.maxHeight = '';
      document.body.classList.remove('how-to-style-modal-open');
      modalBody.innerHTML = '';

      if (lastTrigger) {
        lastTrigger.focus();
        lastTrigger = null;
      }
    }

    // Exposed so shopify:section:unload/:deselect (bound once, outside
    // initSection, below) can force-close this specific root's modal without
    // a separate module-level registry of every initialized root.
    root._htsCloseModal = closeModal;

    root.addEventListener('click', function (event) {
      var openTrigger = event.target.closest('[data-how-to-style-look-template]');
      if (openTrigger) {
        openModal(openTrigger.getAttribute('data-how-to-style-look-template'), openTrigger);
        return;
      }

      var closeTrigger = event.target.closest('[data-how-to-style-close]');
      if (closeTrigger && modal.contains(closeTrigger)) {
        closeModal();
        return;
      }

      var submitButton = event.target.closest('.js-go-cart-add-to-cart');
      if (submitButton && modal.contains(submitButton)) {
        event.preventDefault();

        // Already submitting — ignore the click rather than fire a second request.
        if (submitButton.classList.contains('is-loading')) return;

        var form = submitButton.closest('form');
        if (!form) return;

        // Read the variant id directly from the dedicated input rather than
        // going through window.goCart.addItemToCart — that path calls
        // addItemToCartHandler which opens the Rebuy SmartCart automatically,
        // covering the HTS modal and making the section appear hidden until
        // the customer manually closes the cart drawer.
        var variantInput = form.querySelector('[data-how-to-style-selected-variant-id]');
        var quantityInput = form.querySelector('[name="quantity"]');
        if (!variantInput || !variantInput.value) return;

        var buttonText = submitButton.querySelector('[data-add-to-cart-text]');

        submitButton.classList.add('is-loading');
        submitButton.disabled = true;
        submitButton.setAttribute('aria-busy', 'true');
        if (buttonText) buttonText.textContent = 'Adding…';

        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantInput.value,
            quantity: quantityInput ? (parseInt(quantityInput.value, 10) || 1) : 1
          })
        })
          .then(function () {
            if (buttonText) buttonText.textContent = 'Added!';
          })
          .catch(function () {
            if (buttonText) buttonText.textContent = 'Add to Cart';
            if (window.goCart && typeof window.goCart.ajaxRequestFail === 'function') {
              window.goCart.ajaxRequestFail();
            }
          })
          .finally(function () {
            submitButton.classList.remove('is-loading');
            setTimeout(function () {
              submitButton.disabled = false;
              submitButton.removeAttribute('aria-busy');
              if (buttonText) buttonText.textContent = 'Add to Cart';
            }, 2000);
          });
      }
    });

    root.addEventListener('change', function (event) {
      var input = event.target.closest('.how-to-style-option-input');
      if (!input) return;

      var row = input.closest('.how-to-style-modal-item');
      if (row) updateRowVariant(row, productData, moneyFormat);
    });
  }

  function forceCloseIfOpen(root) {
    var modal = root.querySelector('.how-to-style-modal');
    if (modal && modal.getAttribute('aria-hidden') === 'false' && typeof root._htsCloseModal === 'function') {
      root._htsCloseModal();
    }
  }

  function initAllSectionsWithin(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('.how-to-style-section'), initSection);
  }

  // Document-level listeners (below) MUST be registered before the first
  // initAllSectionsWithin(document) call at the bottom of this block, not
  // after it: initSection's own updateVisibleLooks() call dispatches
  // how-to-style:visibility-changed synchronously, during that very first
  // call — registering the listener afterwards would silently miss it, and
  // the badge would never receive its correct initial state until the
  // first actual variant/color change (caught by testing this file's
  // real, unmodified behavior against real fetched page data, not by
  // inspection).
  //
  // A section reload can cause the browser to re-execute this very file
  // (re-inserting a <script src> re-runs it, cache or not) — guard the
  // document-level registrations themselves with a one-time flag on window,
  // so repeated reloads can never accumulate extra copies of these
  // listeners. The initAllSectionsWithin(document) call still needs to run
  // on every execution (that's what picks up a freshly-swapped root), and
  // it's already safe to repeat — root-level idempotency is handled inside
  // initSection, not here.
  if (!window.howToStyleEditorListenersBound) {
    window.howToStyleEditorListenersBound = true;

    // Covers both a settings change (Shopify AJAX-replaces this section's HTML
    // then dispatches this) and a fresh "Add section" insert — scoped to
    // event.target (that one section's wrapper) so unrelated, already-
    // initialized sections elsewhere on the page are never touched.
    document.addEventListener('shopify:section:load', function (event) {
      if (event.target && typeof event.target.querySelectorAll === 'function') {
        initAllSectionsWithin(event.target);
      }
    });

    document.addEventListener('shopify:section:unload', function (event) {
      if (event.target && typeof event.target.querySelectorAll === 'function') {
        Array.prototype.forEach.call(event.target.querySelectorAll('.how-to-style-section'), forceCloseIfOpen);
      }
    });

    document.addEventListener('shopify:section:deselect', function (event) {
      if (event.target && typeof event.target.querySelectorAll === 'function') {
        Array.prototype.forEach.call(event.target.querySelectorAll('.how-to-style-section'), forceCloseIfOpen);
      }
    });

    // The one deliberate exception to "never bind to document": the PDP's
    // variant/color picker (<variant-radios>/<variant-selects>, per
    // snippets/pdp-variant-picker.liquid) lives outside this section, in the
    // product form, so there's no section-scoped root to delegate to here.
    // Matches the exact selectors assets/product.js's own cr.variantsOnChange
    // binds to, so this fires for the same real user interactions and no
    // others. Only ever reads state and updates each How to Style section's
    // own visibility — never touches anything outside it.
    //
    // Once a cross-product color swap has happened (crossProductState set —
    // see swapHowToStyleSectionForProduct above), a same-product size change
    // no longer means what getSelectedVariantId()/window.variants think it
    // means, since those still describe the page's ORIGINAL product, not
    // the one the customer is now actually looking at (this theme's own
    // color-swatch swap never replaces the size radios themselves). In that
    // state, route size changes through the same section re-fetch instead.
    document.addEventListener('change', function (event) {
      if (!event.target.closest('.product-form__input input[type="radio"], .product-form__input select')) {
        return;
      }

      if (crossProductState) {
        swapHowToStyleSectionForProduct(crossProductState.productUrl);
        return;
      }

      var selectedVariantId = getSelectedVariantId();
      Array.prototype.forEach.call(document.querySelectorAll('.how-to-style-section'), function (root) {
        updateVisibleLooks(root, selectedVariantId);
      });
    });

    // Cross-product color swap trigger — see swapHowToStyleSectionForProduct
    // above for why this needs its own, separate listener rather than
    // reusing the .product-form__input one. Matches the exact selector
    // pdp-variant-picker.liquid's own attachSwatchEventListeners() binds to
    // (target.matches('.color-swatch input')), so this only ever fires for
    // the same real color-swatch clicks that mechanism already handles.
    document.addEventListener('change', function (event) {
      var swatchInput = event.target.closest('.color-swatch input');
      if (!swatchInput || !swatchInput.dataset.productUrl) return;

      swapHowToStyleSectionForProduct(swatchInput.dataset.productUrl);
    });

    // Explicit hook from pdp-variant-picker.liquid's own updateProductDetails()
    // — the single funnel every resolved variant/color/size/inseam change in
    // that custom picker already runs through, including browser back/forward
    // navigation (fetchAndUpdateProduct/popstate). That last case fires no
    // .product-form__input or .color-swatch change event at all, so it's the
    // one real product change the two listeners above can never see on their
    // own — confirmed by reading that file, not assumed. Safe to overlap with
    // the .color-swatch listener above for the same click: swapRequestToken
    // already makes a redundant call harmless (only the latest response is
    // ever applied).
    document.addEventListener('how-to-style:product-changed', function (event) {
      var productUrl = event.detail && event.detail.productUrl;
      if (!productUrl) return;

      swapHowToStyleSectionForProduct(productUrl);
    });

    // "How to Style This" image badge (snippets/pdp-photos.liquid) — purely
    // reactive to the widget's own visibility decision (the
    // how-to-style:visibility-changed event dispatched from
    // updateVisibleLooks above), never a second calculation of its own.
    document.addEventListener('how-to-style:visibility-changed', function (event) {
      var visible = Boolean(event.detail && event.detail.visible);
      Array.prototype.forEach.call(document.querySelectorAll('.how-to-style-image-badge'), function (badge) {
        badge.hidden = !visible;
      });
    });

    document.addEventListener('click', function (event) {
      var badge = event.target.closest('.how-to-style-image-badge');
      if (!badge) return;

      var section = document.querySelector('.how-to-style-section');
      if (!section) return;

      // No manual sticky-header offset math needed: sections/header.liquid's
      // own scroll handler already sets html { scroll-padding-top }, which
      // scrollIntoView respects natively.
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  initAllSectionsWithin(document);
})();
